import { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, FlexGap, Modal, Paragraph, Subheading } from '@treams/treams-ui';

const CONFIRMATION_MODAL_WIDTH = 300;

type ConfirmationAction = {
	title: string;
	buttonType: ComponentProps<typeof Button>['type'];
	onAction: () => Promise<unknown> | unknown;
};

function BaseModal(props: {
	title: string;
	description: string;
	onClose: () => void;
	actions: ConfirmationAction[];
}) {
	const { t } = useTranslation();

	return (
		<Modal width={CONFIRMATION_MODAL_WIDTH}>
			<Modal.Contents>
				<Subheading>{props.title}</Subheading>
				<Paragraph mt={2}>{props.description}</Paragraph>
			</Modal.Contents>
			<Modal.Footer flexDirection="column" alignItems="stretch">
				<FlexGap gap={8} flexDirection="column" alignItems="stretch" flexWrap="wrap">
					{props.actions.map((action, index) => (
						<Button
							key={`${action.title}-${index}`}
							type={action.buttonType}
							title={action.title}
							onClick={async () => {
								await action.onAction();
								props.onClose();
							}}
						/>
					))}
					<Button small type="alternative" title={t('common.cancel')} onClick={props.onClose} />
				</FlexGap>
			</Modal.Footer>
		</Modal>
	);
}

function MeetingSeriesUpdateConfirmationModal(props: {
	onClose: () => void;
	onOnlyThisMeeting: () => Promise<void>;
	onAllFutureMeetings: () => Promise<void>;
}) {
	const { t } = useTranslation();

	return (
		<BaseModal
			title={t('meeting.series.updateConfirmation.title')}
			description={t('meeting.series.updateConfirmation.description')}
			onClose={props.onClose}
			actions={[
				{
					title: t('meeting.series.updateConfirmation.onlyThisMeeting'),
					buttonType: 'primary',
					onAction: props.onOnlyThisMeeting,
				},
				{
					title: t('meeting.series.updateConfirmation.allFutureMeetings'),
					buttonType: 'transparent',
					onAction: props.onAllFutureMeetings,
				},
			]}
		/>
	);
}

function MeetingSeriesArchiveConfirmationModal(props: {
	onClose: () => void;
	onArchive: () => Promise<void>;
}) {
	const { t } = useTranslation();

	return (
		<BaseModal
			title={t('meeting.series.archiveConfirmation.title')}
			description={t('meeting.series.archiveConfirmation.description')}
			onClose={props.onClose}
			actions={[
				{
					title: t('meeting.series.archiveConfirmation.archive'),
					buttonType: 'primary',
					onAction: props.onArchive,
				},
			]}
		/>
	);
}

function MeetingSeriesCancelConfirmationModal(props: {
	onClose: () => void;
	onOnlyThisMeeting: () => Promise<unknown> | unknown;
	onAllFutureMeetings: () => Promise<unknown> | unknown;
}) {
	const { t } = useTranslation();

	return (
		<BaseModal
			title={t('meeting.series.cancelConfirmation.title')}
			description={t('meeting.series.cancelConfirmation.description')}
			onClose={props.onClose}
			actions={[
				{
					title: t('meeting.series.updateConfirmation.onlyThisMeeting'),
					buttonType: 'primary',
					onAction: props.onOnlyThisMeeting,
				},
				{
					title: t('meeting.series.updateConfirmation.allFutureMeetings'),
					buttonType: 'transparent',
					onAction: props.onAllFutureMeetings,
				},
			]}
		/>
	);
}

const MeetingSeriesConfirmationModal = {
	Update: MeetingSeriesUpdateConfirmationModal,
	Archive: MeetingSeriesArchiveConfirmationModal,
	Cancel: MeetingSeriesCancelConfirmationModal,
} as const;

export default MeetingSeriesConfirmationModal;
